/**
 * Shared constants + tiny helpers for the Personal Tax editor.
 * Kept in one place so the RentalsTab / InvestmentsTab / DeductionsTab
 * components + PersonalTaxQuickView + PersonalExpenseToggle all agree.
 */

import { readRate } from "@/tax/rulesets";

/**
 * Live WFH fixed-rate ($/hour). Reads from the versioned ATO knowledge
 * base — falls back to the current confirmed value only if the reader
 * ever returns `undefined` (which happens iff the section is a scaffold,
 * i.e. never in production). Bumping the ruleset next FY cascades to
 * every screen that uses this constant, with zero code changes here.
 */
export const WFH_RATE_PER_HOUR = readRate("wfhFixedRate")?.amount ?? 0.70;
/** Human label — "70c/hr" — derived from the live rate so screens stay in sync. */
export const WFH_RATE_LABEL = `${Math.round(WFH_RATE_PER_HOUR * 100)}c/hr`;

export const SUB_LABEL = {
  // Rental
  rent_gross: "Rent received",
  other_rental_income: "Other rental income",
  agent_fees: "Property agent fees",
  repairs: "Repairs & maintenance",
  depreciation: "Depreciation (capital works)",
  interest: "Interest on loan",
  insurance: "Landlord insurance",
  rates_water: "Council rates & water",
  strata: "Strata / body-corporate",
  cleaning_gardening: "Cleaning & gardening",
  advertising: "Advertising for tenants",
  other_expense: "Other rental expense",
  // Investment
  dividend: "Dividend received",
  franking_credit: "Franking credit",
  interest_income: "Interest income",
  distribution: "Trust distribution",
  // Deductions
  wfh_hours: `Work-from-home hours (${WFH_RATE_LABEL})`,
  tools_under_300: "Tools & equipment <$300",
  self_education: "Self-education",
  uniform_laundry: "Uniform & laundry",
  income_protection: "Income-protection insurance",
  union_fees: "Union / professional subs",
  other: "Other deduction",
};

export const RENTAL_INCOME_SUBS = ["rent_gross", "other_rental_income"];
export const RENTAL_EXPENSE_SUBS = [
  "agent_fees", "repairs", "depreciation", "interest", "insurance",
  "rates_water", "strata", "cleaning_gardening", "advertising", "other_expense",
];
export const INVESTMENT_TYPES = [
  { type: "dividend", sub: "dividend", label: "Dividend" },
  { type: "franking_credit", sub: "franking_credit", label: "Franking credit" },
  { type: "dividend", sub: "interest_income", label: "Interest income" },
  { type: "dividend", sub: "distribution", label: "Trust distribution" },
];
export const DEDUCTION_SUBS = [
  "wfh_hours", "tools_under_300", "self_education", "uniform_laundry",
  "income_protection", "union_fees", "other",
];

export const fmt = (n) =>
  `$${Number(n || 0).toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
